import React from 'react';
import { Shield, Target, Clock, GitGraph, CheckCircle, BarChart, ArrowRight } from 'lucide-react';

const About = () => {
  const advantages = [
    {
      Icon: Shield,
      title: "Industry Experience",
      description: "With over 15 years of experience in rail logistics, we understand the unique challenges faced by rail shippers and operators."
    },
    {
      Icon: Target,
      title: "Customer Focus",
      description: "Our solutions are developed in close collaboration with customers, ensuring we meet real industry needs."
    },
    {
      Icon: GitGraph,
      title: "Innovation",
      description: "We continuously evolve our technology to stay ahead of industry changes and provide cutting-edge solutions."
    },
    {
      Icon: Clock,
      title: "Proven Results",
      description: "Our clients consistently report improved efficiency and significant cost savings after implementing our solutions."
    }
  ];

  const benefits = [
    {
      title: "Reduced Administrative Time",
      description: "Automate routine tasks and streamline workflows to free up your team for strategic activities."
    },
    {
      title: "Enhanced Data Accuracy",
      description: "Eliminate manual data entry errors and ensure consistent information across all systems."
    },
    {
      title: "Improved Asset Utilization",
      description: "Optimize your fleet usage with real-time tracking and intelligent planning tools."
    },
    {
      title: "Better Decision Making",
      description: "Access comprehensive analytics and reporting to make data-driven decisions."
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              The GeoMetrix Advantage
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              At GeoMetrix, we're more than just a software provider. We're your partner in optimizing rail operations through innovative technology solutions. Our deep industry expertise and customer-focused approach set us apart in delivering real value to rail shippers and operators.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section with Background Image */}
      <section className="relative py-24">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/Railcar 2 .jpg"
            alt="Rail operations"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              Our mission is to be the most reliable backend logistics partner in the rail industry — empowering our clients to focus on movement, not paperwork.
            </p>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="relative py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Built on Experience</h2>
              <p className="text-lg text-gray-600 mb-8">
                Since our founding, we've focused exclusively on solving the complex challenges of rail logistics. This specialized focus has allowed us to develop deep industry knowledge and create solutions that address real-world operational needs.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">Specialized focus on rail logistics technology</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">Continuous collaboration with industry leaders</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">Solutions built for real operational challenges</p>
                </div>
              </div>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img 
                src="/images/Railcar 6.jpg"
                alt="Rail operations expertise"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Grid */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose GeoMetrix</h2>
            <p className="text-xl text-gray-600">
              Our commitment to excellence sets us apart
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
                <advantage.Icon className="h-12 w-12 text-blue-600 mb-6" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">{advantage.title}</h3>
                <p className="text-gray-600 leading-relaxed">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Measurable Benefits</h2>
            <p className="text-xl text-gray-600">
              Real results that impact your bottom line
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <BarChart className="h-8 w-8 text-blue-600 mr-4" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Experience the GeoMetrix Advantage?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss how our solutions can transform your rail operations
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center justify-center bg-white text-blue-600 font-bold py-4 px-8 rounded-md hover:bg-blue-50 transition-colors text-lg"
          >
            Contact Us
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;